/**
 * Live — real-time NAV, P&L and positions of the production strategies.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Radio, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip,
  XAxis, YAxis,
} from "recharts";
import { useAuth } from "../App";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { API } from "../components/stats/statsApi";
import { ExposuresPanel, RetCell } from "./StrategyLabPage";
import { MnrActivity } from "../components/MnrActivity";

const COLORS = {
  big_champion: { accent: "text-indigo-300", line: "#818cf8" },
  market_neutral_opt: { accent: "text-fuchsia-300", line: "#e879f9" },
  market_neutral_v2: { accent: "text-cyan-300", line: "#22d3ee" },
  market_neutral_v5: { accent: "text-amber-300", line: "#fbbf24" },
  market_neutral_v6: { accent: "text-emerald-300", line: "#34d399" },
  ea_book: { accent: "text-rose-300", line: "#fb7185" },
  div_book: { accent: "text-teal-300", line: "#2dd4bf" },
  ext_div_book: { accent: "text-orange-300", line: "#fdba74" },
  div_select_book: { accent: "text-lime-300", line: "#a3e635" },
  millenium: { accent: "text-indigo-300", line: "#818cf8" },
  market_neutral_r: { accent: "text-sky-300", line: "#38bdf8" },
};

const DIV_KEYS = ["div_book", "ext_div_book", "div_select_book"];
const DIV_SUB_LABELS = {
  div_book: "Emergent Dividend",
  ext_div_book: "Extreme Dividend",
  div_select_book: "Select 10",
};

const pct = (v, dp = 2) =>
  v == null ? "—" : `${v > 0 ? "+" : ""}${(v * 100).toFixed(dp)}%`;
const usd = (v) =>
  v == null ? "—" : `${v < 0 ? "-" : ""}$${Math.abs(v) >= 1e6
    ? `${(Math.abs(v) / 1e6).toFixed(2)}M`
    : Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const cls = (v) => (v > 0 ? "text-green-400" : v < 0 ? "text-red-400" : "");

const StatCard = ({ label, value, sub, accent, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold font-mono ${accent || ""}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </CardContent>
  </Card>
);

const NavChart = ({ series, color, nav0, benchLabel }) => (
  <div className="h-72">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={series} margin={{ top: 6, right: 12, bottom: 0, left: 0 }}>
        <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8b8fa3" }}
               tickFormatter={(d) => d?.slice(2, 7)} minTickGap={48} />
        <YAxis tick={{ fontSize: 10, fill: "#8b8fa3" }} domain={["auto", "auto"]}
               tickFormatter={(v) => (Math.abs(v) >= 1e6
                 ? `${(v / 1e6).toFixed(0)}M` : `${(v / 1e3).toFixed(1)}k`)} width={48} />
        <Tooltip
          contentStyle={{ background: "#12141c", border: "1px solid rgba(255,255,255,0.1)",
                          borderRadius: 8, fontSize: 12 }}
          formatter={(v, name) => [usd(v), name === "spy" ? benchLabel : "NAV"]} />
        <ReferenceLine y={nav0} stroke="rgba(255,255,255,0.15)" strokeDasharray="3 3" />
        {benchLabel && (
          <Line type="monotone" dataKey="spy" stroke="#7c8494" strokeWidth={1.5}
                strokeDasharray="5 3" dot={false} />
        )}
        <Line type="monotone" dataKey="nav" stroke={color} strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

const ORIGIN_STYLE = {
  champion: "text-amber-300 border-amber-500/40",
  core: "text-sky-300 border-sky-500/40",
  scout: "text-emerald-300 border-emerald-500/40",
  radar: "text-rose-300 border-rose-500/40",
};

const OriginBadge = ({ origin }) => (
  <span className="inline-flex gap-1">
    {origin.split("+").map((o) => (
      <span key={o} className={`text-[9px] uppercase tracking-wide border rounded px-1 py-0.5 ${
        ORIGIN_STYLE[o] || "text-muted-foreground border-border"}`}>
        {o}
      </span>
    ))}
  </span>
);

const mergeLots = (positions) => {
  const m = new Map();
  positions.forEach((p) => {
    const prev = m.get(p.symbol);
    if (!prev) { m.set(p.symbol, { ...p, lots: 1 }); return; }
    const aw = Math.abs(prev.weight) + Math.abs(p.weight);
    if (aw > 0) {
      prev.entry_px = ((prev.entry_px || 0) * Math.abs(prev.weight)
        + (p.entry_px || 0) * Math.abs(p.weight)) / aw;
      prev.since_entry = ((prev.since_entry || 0) * Math.abs(prev.weight)
        + (p.since_entry || 0) * Math.abs(p.weight)) / aw;
    }
    prev.day_pct = p.day_pct ?? prev.day_pct;
    prev.pnl_usd = (prev.pnl_usd || 0) + (p.pnl_usd || 0);
    prev.weight += p.weight;
    prev.lots += 1;
  });
  return [...m.values()];
};

const PositionsTable = ({ positions, sleeveKey }) => {
  const merged = mergeLots(positions);
  const hasOrigin = merged.some((p) => p.origin);
  const hasShares = merged.some((p) => p.shares != null);
  return (
  <div className="overflow-x-auto max-h-96 overflow-y-auto">
    <table className="w-full text-xs" data-testid={`live-positions-${sleeveKey}`}>
      <thead className="border-b border-border text-muted-foreground sticky top-0 bg-card">
        <tr>
          <th className="py-1.5 px-2 text-left">Symbol</th>
          {hasOrigin && <th className="py-1.5 px-2 text-left">Origin</th>}
          <th className="py-1.5 px-2 text-left">Side</th>
          {hasShares && <th className="py-1.5 px-2 text-right">Shares</th>}
          <th className="py-1.5 px-2 text-right">Weight</th>
          <th className="py-1.5 px-2 text-right">Entry</th>
          <th className="py-1.5 px-2 text-right">Last</th>
          <th className="py-1.5 px-2 text-right">Day</th>
          <th className="py-1.5 px-2 text-right">Since entry</th>
          <th className="py-1.5 px-2 text-right">P&L</th>
          <th className="py-1.5 px-2 text-left">Sector</th>
        </tr>
      </thead>
      <tbody>
        {merged.map((p) => (
          <tr key={p.symbol} className="border-b border-border/40">
            <td className="py-1.5 px-2 font-semibold">
              {p.symbol}
              {p.lots > 1 && (
                <span className="ml-1 text-[9px] text-muted-foreground" title={`${p.lots} lots (execution-policy tranches) merged`}>
                  ×{p.lots}
                </span>
              )}
            </td>
            {hasOrigin && (
              <td className="py-1.5 px-2" data-testid={`origin-${p.symbol}`}>
                {p.origin ? <OriginBadge origin={p.origin} /> : "—"}
              </td>
            )}
            <td className="py-1.5 px-2">
              <span className={`text-[10px] font-semibold ${
                p.weight > 0 ? "text-emerald-400" : "text-red-400"}`}>
                {p.weight > 0 ? "LONG" : "SHORT"}
              </span>
            </td>
            {hasShares && (
              <td className="py-1.5 px-2 text-right font-mono" data-testid={`shares-${p.symbol}`}>
                {p.shares}
              </td>
            )}
            <td className="py-1.5 px-2 text-right font-mono">{(Math.abs(p.weight) * 100).toFixed(2)}%</td>
            <td className="py-1.5 px-2 text-right font-mono text-muted-foreground">{p.entry_px?.toFixed(2)}</td>
            <td className="py-1.5 px-2 text-right font-mono">{p.last?.toFixed(2)}</td>
            <td className={`py-1.5 px-2 text-right font-mono ${cls(p.day_pct)}`}>{pct(p.day_pct)}</td>
            <td className={`py-1.5 px-2 text-right font-mono ${cls(p.since_entry * Math.sign(p.weight))}`}>{pct(p.since_entry)}</td>
            <td className={`py-1.5 px-2 text-right font-mono ${cls(p.pnl_usd)}`}>{usd(p.pnl_usd)}</td>
            <td className="py-1.5 px-2 text-muted-foreground">{p.sector || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  );
};

const Chip = ({ sym, w, tone }) => (
  <span className={`border rounded px-1.5 py-0.5 ${
    tone === "in" ? "border-emerald-500/40 text-emerald-300"
                  : "border-rose-500/40 text-rose-300"}`}>
    {sym}{w != null && <span className="text-[9px] text-muted-foreground ml-1">{w}%</span>}
  </span>
);

const MnRebalanceHistory = () => {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    fetch(`${API}/live/mn/rebalances`, {
      headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then(setRows)
      .catch(() => {});
  }, [token]);
  if (!rows.length) return null;
  return (
    <Card data-testid="mn-rebalance-history">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          Rebalance history <span className="text-muted-foreground font-normal">
            (long/short entries & exits at each monthly formation)</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="max-h-96 overflow-y-auto space-y-3">
        {rows.map((r) => (
          <div key={r.date} className="text-xs border-b border-border/40 pb-2"
               data-testid={`mn-rebalance-${r.date}`}>
            <div className="flex items-center gap-3 mb-1">
              <span className="font-mono text-muted-foreground">{r.date}</span>
              <span className="text-muted-foreground">
                {r.n_long}L / {r.n_short}S · turnover {(r.turnover * 100).toFixed(0)}%
              </span>
            </div>
            {[["IN LONG", r.entered_long, "in"], ["IN SHORT", r.entered_short, "in"],
              ["OUT LONG", r.exited_long, "out"], ["OUT SHORT", r.exited_short, "out"]]
              .map(([lbl, list, tone]) => (
              <div key={lbl} className="flex flex-wrap gap-1 items-center mb-1">
                <span className={`font-semibold w-16 shrink-0 ${
                  tone === "in" ? "text-emerald-400" : "text-rose-400"}`}>{lbl}</span>
                {list.length ? list.map((p) => (
                  <Chip key={p.symbol} sym={p.symbol} w={p.weight} tone={tone} />
                )) : <span className="text-muted-foreground">—</span>}
              </div>
            ))}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const RebalanceHistory = () => {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    fetch(`${API}/live/alpha-select/rebalances`, {
      headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then(setRows)
      .catch(() => {});
  }, [token]);
  if (!rows.length) return null;
  return (
    <Card data-testid="alpha-rebalance-history">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          Rebalance history <span className="text-muted-foreground font-normal">
            (names entered / exited at each monthly formation)</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="max-h-96 overflow-y-auto space-y-2">
        {rows.map((r) => (
          <div key={r.date}
               className="flex flex-wrap items-start gap-2 text-xs border-b border-border/40 pb-2"
               data-testid={`alpha-rebalance-${r.date}`}>
            <span className="font-mono text-muted-foreground w-20 pt-0.5">{r.date}</span>
            <div className="flex-1 space-y-1">
              <div className="flex flex-wrap gap-1 items-center">
                <span className="text-emerald-400 font-semibold w-8">IN</span>
                {r.entered.length ? r.entered.map((p) => (
                  <span key={p.symbol}
                        className="border border-emerald-500/40 text-emerald-300 rounded px-1.5 py-0.5">
                    {p.symbol}
                    <span className="text-[9px] text-muted-foreground ml-1 uppercase">{p.origin}</span>
                  </span>
                )) : <span className="text-muted-foreground">—</span>}
              </div>
              <div className="flex flex-wrap gap-1 items-center">
                <span className="text-rose-400 font-semibold w-8">OUT</span>
                {r.exited.length ? r.exited.map((p) => (
                  <span key={p.symbol}
                        className="border border-rose-500/40 text-rose-300 rounded px-1.5 py-0.5">
                    {p.symbol}
                  </span>
                )) : <span className="text-muted-foreground">—</span>}
              </div>
            </div>
            <span className="text-muted-foreground pt-0.5">
              {r.unchanged}/{r.n_book} held
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const MonthlyTable = ({ monthly, hasBench, sleeveKey }) => (
  <div className="overflow-x-auto max-h-96 overflow-y-auto">
    <table className="w-full text-xs" data-testid={`live-monthly-${sleeveKey}`}>
      <thead className="border-b border-border text-muted-foreground sticky top-0 bg-card">
        <tr>
          <th className="py-1.5 px-2 text-left">Month</th>
          <th className="py-1.5 px-2 text-right">Return</th>
          {hasBench && <th className="py-1.5 px-2 text-right">SPY</th>}
          {hasBench && <th className="py-1.5 px-2 text-right">Excess</th>}
        </tr>
      </thead>
      <tbody>
        {[...monthly].reverse().map((m) => (
          <tr key={m.month} className="border-b border-border/40">
            <td className="py-1.5 px-2 font-mono whitespace-nowrap">{m.month}</td>
            <RetCell v={m.ret} dp={2} />
            {hasBench && <RetCell v={m.spy} dp={2} />}
            {hasBench && <RetCell v={m.excess} dp={2} />}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const EaTradeLog = () => {
  const { token } = useAuth();
  const [log, setLog] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/live/ea/trade-log`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok && !cancelled) setLog(await r.json());
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [token]);
  if (!log) return <p className="text-xs text-muted-foreground">Loading trade log…</p>;
  return (
    <div className="overflow-x-auto max-h-96 overflow-y-auto">
      <table className="w-full text-xs" data-testid="live-ea-tradelog">
        <thead className="border-b border-border text-muted-foreground sticky top-0 bg-card">
          <tr>
            <th className="py-1.5 px-2 text-left">Exit</th>
            <th className="py-1.5 px-2 text-left">Symbol</th>
            <th className="py-1.5 px-2 text-left">Print</th>
            <th className="py-1.5 px-2 text-right">Buyers</th>
            <th className="py-1.5 px-2 text-right">Net hdg</th>
            <th className="py-1.5 px-2 text-left">Src</th>
          </tr>
        </thead>
        <tbody>
          {log.trades.map((t, i) => (
            <tr key={`${t.symbol}-${t.date}-${i}`} className="border-b border-border/40"
                title={(t.funds || []).join(", ")}>
              <td className="py-1.5 px-2 font-mono whitespace-nowrap text-muted-foreground">{t.date}</td>
              <td className="py-1.5 px-2 font-semibold">{t.symbol}</td>
              <td className="py-1.5 px-2">
                <span className="text-[9px] uppercase tracking-wide border border-border rounded px-1 py-0.5 text-muted-foreground">
                  {t.timing || "—"}
                </span>
              </td>
              <td className="py-1.5 px-2 text-right font-mono">{t.n_conv ?? "—"}</td>
              <RetCell v={t.net_hdg} dp={2} />
              <td className="py-1.5 px-2">
                <span className={`text-[9px] uppercase tracking-wide border rounded px-1 py-0.5 ${
                  t.source === "live" ? "text-emerald-300 border-emerald-500/40"
                                      : "text-muted-foreground border-border"}`}>
                  {t.source === "live" ? "live" : "bt"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CoversTable = ({ covers, sleeveKey }) => (
  <Card data-testid={`live-covers-${sleeveKey}`}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm">
        Covered shorts <span className="text-muted-foreground font-normal">(rolled into SPY short)</span>
      </CardTitle>
    </CardHeader>
    <CardContent>
      <table className="w-full text-xs">
        <thead className="border-b border-border text-muted-foreground">
          <tr>
            <th className="py-1.5 px-2 text-left">Symbol</th>
            <th className="py-1.5 px-2 text-left">Date</th>
            <th className="py-1.5 px-2 text-right">Cover px</th>
            <th className="py-1.5 px-2 text-left">Trigger</th>
          </tr>
        </thead>
        <tbody>
          {[...covers].reverse().map((cv, i) => (
            <tr key={`${cv.symbol}-${i}`} className="border-b border-border/40">
              <td className="py-1.5 px-2 font-semibold">{cv.symbol}</td>
              <td className="py-1.5 px-2 font-mono text-muted-foreground">{cv.date}</td>
              <td className="py-1.5 px-2 text-right font-mono">{cv.cover_px?.toFixed(2)}</td>
              <td className="py-1.5 px-2">
                <Badge variant="outline" className={`text-[10px] ${
                  cv.reason === "flow" ? "text-cyan-300 border-cyan-500/40"
                                       : "text-amber-300 border-amber-500/40"}`}>
                  {cv.reason === "flow" ? "OPTIONS FLOW" : "GAP +40%"}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </CardContent>
  </Card>
);

const MN_POLICY = [
  {
    title: "1 · Signal & Universe",
    items: [
      "13F specialist cohorts (graded funds) drive the monthly long/short candidate books — formations at each month's final weekly options observation (executed next trading day) from the latest converged holdings estimates.",
      "Every candidate must clear a $20M real average-daily-volume floor (21-day median dollar volume, FMP real volumes).",
    ],
  },
  {
    title: "2 · Portfolio Construction",
    items: [
      "Barra-style mean-risk optimizer (factor covariance + specific risk) with beta ±0.05, sector bands, correlation-cluster and industry concentration caps, and macro-proxy nets capped at ±10% NAV per factor (gold, silver, oil, rates, USD, credit, copper).",
      "Short family blend 70/15/15: core weak+Hot shorts plus F1 fundamentals-deterioration and F3b short-interest-momentum baskets.",
      "SOFT ensemble filter: every short scaled by the fraction of 20 jittered filter configurations (mega-cap 93–97th pct, quality 68–82nd, late-short −14/−26% over 50–80d) that keep it.",
      "50bps minimum position floor — sub-50bps dust dropped, weight redeployed pro-rata within the same side.",
    ],
  },
  {
    title: "3 · Execution Policy (champion, validated Aug-30)",
    items: [
      "PARTIAL REBALANCE α=0.8: at each formation the book trades only 80% of the gap between held and target weights. Cuts turnover ~35% and slippage ~40% — the monthly signal is slow, so the alpha give-up is minimal.",
      "COVER BLACKOUT 60d: names covered by the flow/stop rules cannot be re-shorted for 60 days. Adds gross alpha (+8pts over the backtest) — covered names remain dangerous for ~2–3 months.",
      "Post-blend 50bps floor: exit tails below 50bps complete immediately; entries snap up to at least 50bps.",
      "All fills sliced at ≤5% of real ADV per day over ≤3 days per position.",
      "Validated end-to-end: gross +70.3%/Sharpe 2.49/DD −4.1% vs full-rebalance +61.6%/2.18/−5.3%; net-of-friction at $150M +50.2%/1.91 vs +34.0%/1.33 (10-draw Monte Carlo).",
    ],
  },
  {
    title: "4 · Risk Guards (continuous)",
    items: [
      "Options-flow covers: shorts whose call-flow moneyness enters the weekly top quintile are covered next session, proceeds redeployed pro-rata.",
      "+25% hard stop on every short vs entry.",
      "Net drift band ±0.20 vs formation net — overflow hedged into SPY.",
      "Give-back guard: after a +3% trailing 10-day run the book de-grosses to 50% for 5 sessions.",
    ],
  },
];

const MnPolicyCard = () => (
  <Card data-testid="mn-policy-card">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm">Process & Execution Policy</CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      {MN_POLICY.map((sec) => (
        <div key={sec.title} data-testid={`mn-policy-${sec.title[0]}`}>
          <div className="text-xs font-semibold text-amber-300 mb-1.5">{sec.title}</div>
          <ul className="space-y-1">
            {sec.items.map((it, i) => (
              <li key={i} className="text-xs text-muted-foreground flex gap-2">
                <span className="text-amber-500/60 shrink-0">—</span>
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </CardContent>
  </Card>
);

const MN_CAPACITY = [
  { nav: "$150M", net: "+49.8%", rng: "[+43.1 .. +54.6]", ann: "16.3%/yr", sh: "1.90", dd: "-4.5%", slip: "354" },
  { nav: "$500M", net: "+42.2%", rng: "[+32.5 .. +49.5]", ann: "14.1%/yr", sh: "1.66", dd: "-4.7%", slip: "573" },
  { nav: "$1B", net: "+36.8%", rng: "[+25.4 .. +45.5]", ann: "12.4%/yr", sh: "1.48", dd: "-5.0%", slip: "720" },
];

const MnCapacityCard = () => (
  <Card data-testid="mn-capacity-card">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm">
        Capacity profile <span className="text-muted-foreground font-normal">(net of all frictions, Dec-23 → Aug-26)</span>
      </CardTitle>
    </CardHeader>
    <CardContent>
      <table className="w-full text-xs" data-testid="mn-capacity-table">
        <thead className="border-b border-border text-muted-foreground">
          <tr>
            <th className="py-1.5 px-2 text-left">Book size</th>
            <th className="py-1.5 px-2 text-right">Net total</th>
            <th className="py-1.5 px-2 text-right">10-sim range</th>
            <th className="py-1.5 px-2 text-right">Annualized</th>
            <th className="py-1.5 px-2 text-right">Sharpe</th>
            <th className="py-1.5 px-2 text-right">MaxDD</th>
            <th className="py-1.5 px-2 text-right">Slippage</th>
          </tr>
        </thead>
        <tbody>
          {MN_CAPACITY.map((r) => (
            <tr key={r.nav} className="border-b border-border/40">
              <td className="py-1.5 px-2 font-semibold">{r.nav}</td>
              <td className="py-1.5 px-2 text-right font-mono text-emerald-400">{r.net}</td>
              <td className="py-1.5 px-2 text-right font-mono text-muted-foreground">{r.rng}</td>
              <td className="py-1.5 px-2 text-right font-mono">{r.ann}</td>
              <td className="py-1.5 px-2 text-right font-mono">{r.sh}</td>
              <td className="py-1.5 px-2 text-right font-mono text-red-400">{r.dd}</td>
              <td className="py-1.5 px-2 text-right font-mono">{r.slip} bps/yr</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[11px] text-muted-foreground mt-3">
        Gross backtest +70.3% (Sharpe 2.49, DD −4.1%) is book-size independent.
        Net figures from a 10-draw Monte Carlo: commissions 1–3bps, square-root
        market impact k 0.3–0.8 (calibrated to institutional realized costs),
        ADV-tiered borrow 25bps–8%/yr, fills sliced ≤10% of real ADV per day
        over ≤10 days (all trades actually fill — honest capacity read).
      </p>
    </CardContent>
  </Card>
);

const HoldingChip = ({ sleeveKey, holding }) => {
  const h = holding?.sleeves?.[sleeveKey];
  if (!h || (!h.long && !h.short)) return null;
  const fmt = (x) => `${Math.round(x.mean_days)}d`;
  return (
    <Badge variant="outline" className="font-mono text-[10px] text-muted-foreground"
           data-testid={`live-holding-${sleeveKey}`}
           title={`Average holding period (calendar days). Long: mean ${h.long?.mean_days}d, median ${h.long?.median_days}d${h.short ? ` · Short: mean ${h.short.mean_days}d, median ${h.short.median_days}d (covers included)` : ""}`}>
      avg hold{h.long ? ` · long ${fmt(h.long)}` : ""}{h.short ? ` · short ${fmt(h.short)}` : ""}
    </Badge>
  );
};

const SleeveLive = ({ s, holding }) => {
  const c = COLORS[s.key] || {};
  const st = s.stats || {};
  const live = s.state || {};
  const alerts = live.alerts || [];
  const chartData = React.useMemo(() => {
    if (!s.benchmark) return s.nav_series;
    const spyByDate = Object.fromEntries(s.benchmark.series.map((p) => [p.date, p.nav]));
    return s.nav_series.map((p) => ({ ...p, spy: spyByDate[p.date] }));
  }, [s]);
  const exForView = React.useMemo(() => {
    const ex = s.book?.exposures;
    if (s.key !== "market_neutral_v6" || !ex) return ex;
    const b = live.band || {};
    const h = b.hedge_w ?? 0;
    return {
      ...ex,
      spy_hedge: (ex.spy_hedge || 0) + h,
      net: b.net_base != null ? b.net_base + h : ex.net,
      beta_ex_ante: b.beta_total ?? ex.beta_ex_ante,
    };
  }, [s, live]);
  return (
    <div className="space-y-4" data-testid={`live-sleeve-${s.key}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className={`text-lg font-bold ${c.accent}`}>{s.label}</h2>
        <Badge variant="outline" className="font-mono text-[10px]">
          book {usd(s.nav0)} · formed {live.formation_date || s.book?.formation_date}
        </Badge>
        <HoldingChip sleeveKey={s.key} holding={holding} />
        {live.market_open && (
          <span className="flex items-center gap-1.5 text-[11px] text-emerald-400">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> market open
          </span>
        )}
      </div>

      {s.key === "ea_book" && (
        <p className="text-xs text-muted-foreground" data-testid="live-ea-note">
          Event book from the Earnings Alpha engine (whitepaper v2.1): long into
          the print when a skilled-cohort fund (8-quarter formation) has opened a
          new position in an under-crowded (≤5 buyers), volatile, ≥$2B name —
          one session per trade, 5% of NAV per signal, subsector-hedged net of
          30bp. History is the clean-panel backtest book (+127bp/trade, t = 5.3,
          Sharpe 2.21, maxDD −15.2%); forward NAV accrues from settled live
          signals at each close. Positions below are signals currently in (or
          entering) their holding session.
        </p>
      )}

      {s.key === "market_neutral_r" && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground" data-testid="live-mnr-note">
          Retail copy of Market Neutral for a $10,000 margin account, whole shares only
          (Version A, full copy, always full size — the give-back guard is not copied).
          On every institutional trade day — short covers or the monthly formation — share
          counts are re-optimised toward 0.9× the institutional weights (factor-risk tracking
          vs spread cost, gross ≤ 1.9×, SPYM as the S&amp;P hedge). Orders are emailed at
          18:00 UK only on days with orders and the book assumes fills at that day's close;
          borrow 1%/yr is accrued on shorts, dividends are not modelled. History is the
          backtest under the same live convention (covers same day, formations one day late):
          $10,000 → $15,281 from Dec-23, +16.1%/yr, Sharpe 1.99, max DD −4.5%.
          </p>
          {live.cash != null && (
            <Badge variant="outline" data-testid="live-mnr-cash-badge"
                   className="text-muted-foreground border-border">
              always full size (no guard) · cash {usd(live.cash)}
            </Badge>
          )}
        </div>
      )}

      {s.key === "div_book" && (
        <p className="text-xs text-muted-foreground" data-testid="live-div-note">
          Emergent Dividend book — walk-forward income-specialist cohort
          (EB-graded on all their ≥2%-yield buys) opening NEW positions in ≥5%
          trailing-yield, ≥$2B names whose holder base has avg ia_skew ≥ median
          AND whose entry-yield drift (filing yield ÷ quarter-end yield) sits in
          the stable 0.95–1.10 band — rallied-away and leak-suspect names are
          skipped (div7). V5 sizing: weights ∝ current trailing yield,
          rebalanced monthly + on entry/exit days (no daily reweight), 63-session
          holds — longer holds, yield-spike stops, dividend-cut exits and
          ex-div timing all tested &amp; rejected (div8–div11). Study stats:
          +198.4% total, Sharpe 1.50, +138.9% cum vs SCHD, OOS +98.2%.
          History is the walk-forward replay; book mirrors the
          backtest's open positions, marked to market live.
        </p>
      )}

      {s.key === "ext_div_book" && (
        <p className="text-xs text-muted-foreground" data-testid="live-extdiv-note">
          Extreme Dividend book — "trap divers". The ≥8% trailing-yield zone is
          toxic for the average fund (−152bp/event OOS), but funds EB-graded on
          their own past extreme-yield buys flip it: +364bp/event OOS. Runs the
          ≥$1B variant with the drift 0.90–1.15 band (div7: drift 1.15–1.30 =
          information-leak zone, −847bp/event): study book +131% total, +83.3%
          cum vs SCHD, OOS +73.6%. Thin by nature (~4-6 names); ≥10% yield
          stays excluded — unrescuable even for this cohort. Equal weight,
          63-session holds, vs SCHD.
        </p>
      )}

      {s.key === "div_select_book" && (
        <p className="text-xs text-muted-foreground" data-testid="live-select-note">
          Dividend Select 10 — the concentrated monthly book: 5 emergent names
          (≥5% yield + ia_skew ≥ median, ranked by holder-base ia_skew) + 5
          extreme names (≥8% trap-diver cohort, ranked by the buying fund's
          point-in-time EB grade), both pools pre-filtered by the div7 yield-
          drift bands, max 2 names per sector across the 10, 10%
          each, rebalanced on the first trading day of each month; when a pool
          runs thin, slots are backfilled from the other pool (div6-validated:
          OOS Sharpe 0.93 vs 0.83 for cash-drag). Honest read: the 10-name
          concentration gives up most of the diversified books' excess — replay
          +65% total, ~flat vs SCHD OOS — it trades breadth for simplicity.
        </p>
      )}

      {s.key === "millenium" && (
        <p className="text-xs text-muted-foreground" data-testid="live-millenium-note">
          Millenium — the flagship Market Neutral book run at $150M with gross
          leverage 1.0 (0.5× long / 0.5× short vs the base book's 1×/1×).
          Derived live from Market Neutral: identical signals, covers, guard and
          rebalancing — every daily return scaled ×0.5, full history since
          Dec-2023 inception. Position weights and P&L shown at the scaled
          exposures.
        </p>
      )}

      {s.key === "market_neutral_v2" && (
        <p className="text-xs text-muted-foreground" data-testid="live-v2-note">
          Same monthly book as Market Neutral, plus proactive options-flow covers:
          shorts whose call-flow moneyness enters the weekly cross-sectional top
          quintile are rolled into a SPY short the next session (backtest, weekly
          cadence: Sharpe 2.12 vs 1.79, vol 8.0% vs 9.1%). Forward validation sleeve.
        </p>
      )}

      {s.key === "market_neutral_v5" && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground" data-testid="live-v5-note">
            Monthly long/short book from the 13F specialist cohorts. Short leg
            runs the 70/15/15 family blend (core weak+Hot shorts plus F1
            fundamentals-deterioration and F3b short-interest-momentum baskets)
            with the SOFT ensemble filter: every short is scaled by the
            fraction of 20 jittered filter configurations (mega-cap percentile
            93–97, quality quantile 68–82, late-short −14/−26% over 50–80
            days) that keep it — graded exclusions remove threshold cliffs and
            parameter selection bias. Positions pass a $20M real-ADV floor, a
            3-day/5%-participation real-ADV size cap and a 50bps minimum
            position floor. Rebalances follow the champion execution policy
            (validated Aug-30): trade only 80% of the gap to target each month
            and never re-short a name covered within the last 60 days — full
            details in the Process &amp; Execution Policy section below.
            Formation caps macro
            nets at ±10% NAV per factor. Options-flow covers, +25% short
            hard-stop, ±0.20 net band, and the give-back guard: after a +3%
            trailing 10-day run the book de-grosses to 50% for 5 sessions.
          </p>
          {live.guard && (
            <Badge variant="outline" data-testid="live-v5-guard-badge"
                   className={live.guard.active
                     ? "text-amber-300 border-amber-500/50"
                     : "text-muted-foreground border-border"}>
              {live.guard.active
                ? `GUARD ACTIVE — ${Math.round((live.guard.scale || 0.5) * 100)}% gross, ${live.guard.days_left}d left`
                : `guard idle — 10d ret ${((live.guard.own10 || 0) * 100).toFixed(1)}% / +3% trigger`}
            </Badge>
          )}
        </div>
      )}

      {s.key === "market_neutral_v6" && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground" data-testid="live-v6-note">
            Derived sleeve: the same champion Market Neutral book, with the
            ESTIMATED book beta (trailing 126-day per-stock betas, point-in-time)
            held inside a 0 to +0.10 band at all times — whenever the drifted
            book's beta leaves the band it is hedged back to the nearest edge
            with a daily SPY overlay (2 bps/side on hedge trades); dollars float.
            Point-in-time replay Dec-23 → Aug-26: +15.8%/yr net, Sharpe 1.93,
            max DD −5.2%, 77% positive months, realised β +0.06, worst month
            −3.1%. The band costs ~4.3pp/yr vs the production ±20% net-drift cap
            (t=2.6) but guarantees the beta is never negative and never above
            0.10 — tracked live to settle the neutrality debate with a forward
            tape.
          </p>
          {live.band && (
            <Badge variant="outline" data-testid="live-v6-band-badge"
                   className="text-emerald-300 border-emerald-500/50 font-mono text-[10px]">
              β est {(live.band.beta_total ?? 0).toFixed(2)} / band{" "}
              {(live.band.lo ?? 0).toFixed(1)}–{(live.band.hi ?? 0.1).toFixed(1)} · SPY hedge{" "}
              {((live.band.hedge_w ?? 0) * 100).toFixed(1)}% · net{" "}
              {((live.band.net_base ?? 0) * 100).toFixed(1)}%
            </Badge>
          )}
        </div>
      )}

      {alerts.length > 0 && (
        <Card className="border-amber-500/40" data-testid={`live-alerts-${s.key}`}>
          <CardContent className="p-3 space-y-1">
            {alerts.map((a, i) => (
              <div key={i} className={`text-xs flex items-center gap-2 ${
                a.level === "cover" ? "text-red-400" : "text-amber-400"}`}>
                <AlertTriangle className="w-3.5 h-3.5" /> {a.msg}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid={`live-stats-${s.key}`}>
        <StatCard label="NAV" value={usd(st.nav_now)} accent={c.accent}
                  sub={`started ${usd(s.nav0)}`} testId={`live-nav-${s.key}`} />
        <StatCard label="Day P&L" value={usd(live.day_pnl)}
                  accent={cls(live.day_pnl)}
                  sub={pct(live.day_pct)} testId={`live-daypnl-${s.key}`} />
        <StatCard label="MTD" value={pct(st.mtd)} accent={cls(st.mtd)}
                  testId={`live-mtd-${s.key}`} />
        <StatCard label="Total return" value={pct(st.total_return, 1)}
                  accent={cls(st.total_return)}
                  sub={`since ${new Date(s.nav_series[0].date).toLocaleString("en-US", { month: "short", year: "numeric" })}`}
                  testId={`live-total-${s.key}`} />
        {s.benchmark && (
          <StatCard label={`vs ${s.benchmark.label.replace(" TR", "")}`}
                    value={pct(st.excess_vs_spx, 1)}
                    accent={cls(st.excess_vs_spx)}
                    sub={`${s.benchmark.label} ${pct(s.benchmark.total_return, 1)}`}
                    testId={`live-excess-${s.key}`} />
        )}
        <StatCard label="Annualized" value={pct(st.ann_return, 1)}
                  testId={`live-ann-${s.key}`} />
        <StatCard label="Current block" value={pct(st.since_formation)}
                  accent={cls(st.since_formation)}
                  sub={`${live.n_positions ?? s.book?.positions?.length ?? "—"} positions`}
                  testId={`live-block-${s.key}`} />
      </div>

      <Card>
        <CardHeader className="pb-1 flex flex-row items-center justify-between">
          <CardTitle className="text-sm">NAV</CardTitle>
          {s.benchmark && (
            <span className="text-[10px] font-mono text-muted-foreground flex items-center gap-1.5">
              <span className="inline-block w-4 border-t border-dashed" style={{ borderColor: "#7c8494" }} />
              {s.benchmark.label} (same capital)
            </span>
          )}
        </CardHeader>
        <CardContent>
          <NavChart series={chartData} color={c.line} nav0={s.nav0}
                    benchLabel={s.benchmark?.label} />
        </CardContent>
      </Card>

      <div>
        <div className="text-[11px] uppercase font-semibold text-muted-foreground mb-2">
          Current exposures — book formed {s.book?.formation_date}
          {s.key === "market_neutral_v6" &&
            " · net, SPY hedge & β include the live β-band overlay; sector/factor tables are the base book"}
        </div>
        <ExposuresPanel ex={exForView} sleeveKey={`live-${s.key}`}
                        vsSpy={s.benchmark?.label === "S&P 500 TR"} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <Card className="xl:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              {s.key === "ea_book" ? (
                <>Trade log <span className="text-muted-foreground font-normal">
                  (per-trade net hedged return, newest first)</span></>
              ) : "Monthly performance"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {s.key === "ea_book"
              ? <EaTradeLog />
              : <MonthlyTable monthly={s.monthly || []} hasBench={!!s.benchmark}
                              sleeveKey={s.key} />}
          </CardContent>
        </Card>

        <Card className="xl:col-span-3">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              Positions <span className="text-muted-foreground font-normal">
                (live prices{live.last_tick ? `, tick ${live.last_tick.slice(11, 16)} UTC` : ""})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {s.key === "market_neutral_r" && !(live.positions || []).length ? (
              <p className="text-xs text-muted-foreground" data-testid="live-mnr-all-cash">
                All cash ({usd(live.cash ?? st.nav_now)}) — the first order day builds the book.
              </p>
            ) : <PositionsTable positions={live.positions || []} sleeveKey={s.key} />}
          </CardContent>
        </Card>
      </div>

      {s.key === "alpha_select" && <RebalanceHistory />}
      {s.key === "market_neutral_r" && <MnrActivity />}
      {s.key === "market_neutral_v5" && (
        <>
          <MnRebalanceHistory />
          <MnPolicyCard />
          <MnCapacityCard />
        </>
      )}
      {s.key === "market_neutral_v6" && (
        <>
          <MnRebalanceHistory />
          <MnPolicyCard />
        </>
      )}

      {(s.book?.covers?.length > 0) && (
        <CoversTable covers={s.book.covers} sleeveKey={s.key} />
      )}
    </div>
  );
};

export default function LivePage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [holding, setHolding] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("big_champion");
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/live/summary`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 404) { setError("not_initialized"); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
      setError(null);
    } catch (e) {
      if (!data) setError(String(e.message || e));
    }
  }, [token, data]);

  const init = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/live/init`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` } });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || "init failed");
      toast.success("Live mode initialized");
      await load();
    } catch (e) {
      toast.error(String(e.message || e));
    } finally { setBusy(false); }
  };

  const tick = async () => {
    setBusy(true);
    try {
      await fetch(`${API}/live/tick`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` } });
      await load();
      toast.success("Prices refreshed");
    } catch { /* noop */ } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!token) return;
    load();
    timer.current = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(timer.current);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!token) return;
    let tries = 0;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch(`${API}/live/holding-periods`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const body = await r.json();
        if (cancelled) return;
        if (body.sleeves && Object.keys(body.sleeves).length) setHolding(body);
        if (body.status && tries < 12) {
          tries += 1;
          setTimeout(poll, 20000);
        }
      } catch { /* noop */ }
    };
    poll();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="p-6 space-y-8 max-w-[1500px]" data-testid="live-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Radio className="w-6 h-6 text-red-400" /> Live
          </h1>
          <p className="text-sm text-muted-foreground">
            Live NAV and P&L of the production strategies — prices refresh every 5
            minutes during market hours, NAV closes at 21:35 UTC. Books re-form on
            each month's final weekly options observation, executed the next
            trading day (same policy as the full historical replay).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={tick} disabled={busy || !data}
                  data-testid="live-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${busy ? "animate-spin" : ""}`} />
            Refresh prices
          </Button>
        </div>
      </div>

      {error === "not_initialized" && (
        <Card data-testid="live-init-card">
          <CardContent className="p-8 text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              The live engine has not been initialized yet.
            </p>
            <Button onClick={init} disabled={busy} data-testid="live-init-btn">
              {busy ? "Initializing…" : "Initialize live mode"}
            </Button>
          </CardContent>
        </Card>
      )}
      {error && error !== "not_initialized" && (
        <p className="text-sm text-red-400">{error}</p>
      )}

      {data && (
        <>
          <div className="flex gap-2 flex-wrap" data-testid="live-view-switcher">
            {data.sleeves.map((s) => (
              <button key={s.key} onClick={() => setView(s.key)}
                      data-testid={`live-view-${s.key}`}
                      className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                        view === s.key
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:text-foreground"}`}>
                {s.label}
              </button>
            ))}
          </div>
          <SleeveLive holding={holding}
            s={data.sleeves.find((x) => x.key === view) || data.sleeves[0]} />
        </>
      )}
    </div>
  );
}
